import React from 'react';
import {
  Star,
  Redeem,
  EmojiEvents,
  LocalOffer,
  CardGiftcard,
  Favorite,
  LocationOn,
  AccessTime,
  Restaurant,
  ShoppingCart,
  AccountCircle,
  Notifications,
  Settings,
  Home,
  Search,
  Menu,
  TrendingUp,
  Whatshot,
  LocalFireDepartment,
  Grade,
  Category,
  Fastfood,
  LunchDining,
  DinnerDining,
  LocalPizza,
  LocalDining,
  Cake,
  Coffee,
  Liquor,
  RamenDining,
  SetMeal,
  TapasSharp,
  MenuBook,
  DeliveryDining,
  KebabDining,
  EmojiFoodBeverage,
  LocalBar,
  SoupKitchen,
  Storefront,
  Discount,
  NewReleases,
  Bolt,
} from '@mui/icons-material';

interface GetIconProps {
  iconName: string;
  color: string;
  className?: string;
}

/**
 * Get Material-UI icon component based on icon name
 * @param iconName - Name of the icon (case-insensitive)
 * @param color - Hex color for the icon
 * @param className - Optional CSS class name
 * @returns React element of the corresponding icon
 */
export const getIcon = ({ iconName, color, className = 'icon' }: GetIconProps): JSX.Element => {
  const iconProps = {
    className,
    style: { color }
  };

  const iconMap: { [key: string]: JSX.Element } = {
    // Rating & Favorites
    star: <Star {...iconProps} />,
    favorite: <Favorite {...iconProps} />,
    grade: <Grade {...iconProps} />,

    // Rewards & Offers
    redeem: <Redeem {...iconProps} />,
    trophy: <EmojiEvents {...iconProps} />,
    offer: <LocalOffer {...iconProps} />,
    gift: <CardGiftcard {...iconProps} />,
    discount: <Discount {...iconProps} />,

    // Food Categories
    restaurant: <Restaurant {...iconProps} />,
    fastfood: <Fastfood {...iconProps} />,
    lunch: <LunchDining {...iconProps} />,
    dinner: <DinnerDining {...iconProps} />,
    pizza: <LocalPizza {...iconProps} />,
    dining: <LocalDining {...iconProps} />,
    cake: <Cake {...iconProps} />,
    coffee: <Coffee {...iconProps} />,
    liquor: <Liquor {...iconProps} />,
    ramen: <RamenDining {...iconProps} />,
    setmeal: <SetMeal {...iconProps} />,
    tapas: <TapasSharp {...iconProps} />,
    menubook: <MenuBook {...iconProps} />,
    delivery: <DeliveryDining {...iconProps} />,
    kebab: <KebabDining {...iconProps} />,
    beverage: <EmojiFoodBeverage {...iconProps} />,
    bar: <LocalBar {...iconProps} />,
    soup: <SoupKitchen {...iconProps} />,
    storefront: <Storefront {...iconProps} />,

    // Trending & Popular
    trending: <TrendingUp {...iconProps} />,
    fire: <LocalFireDepartment {...iconProps} />,
    hot: <Whatshot {...iconProps} />,
    new: <NewReleases {...iconProps} />,
    bolt: <Bolt {...iconProps} />,

    // Navigation & UI
    cart: <ShoppingCart {...iconProps} />,
    account: <AccountCircle {...iconProps} />,
    notifications: <Notifications {...iconProps} />,
    settings: <Settings {...iconProps} />,
    home: <Home {...iconProps} />,
    search: <Search {...iconProps} />,
    menu: <Menu {...iconProps} />,
    category: <Category {...iconProps} />,

    // Location & Time
    location: <LocationOn {...iconProps} />,
    time: <AccessTime {...iconProps} />,
  };

  // Return the icon or default to star if not found
  return iconMap[iconName.toLowerCase()] || <Star {...iconProps} />;
};

/**
 * Get icon background colors based on primary color
 * @param primaryColor - Primary color in hex format
 * @returns Object with background color and light background color
 */
export const getIconBackgroundColors = (primaryColor: string): { bgColor: string; bgLight: string } => {
  // Convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgb = hexToRgb(primaryColor);

  if (rgb) {
    return {
      bgColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`,
      bgLight: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.08)`
    };
  }

  // Fallback if hex conversion fails
  return {
    bgColor: `${primaryColor}15`,
    bgLight: `${primaryColor}10`
  };
};


// ── iconUtils.ts ─────────────────────────────────────────────────────────────

const ICON_SIZE   = 40;
const ICON_PAD    = 6;
const ICON_RADIUS = 8;

/** Draw a rounded-square background on a canvas context */
const drawRoundedBg = (ctx: CanvasRenderingContext2D, color: string, size: number, radius: number) => {
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.lineTo(size - radius, 0);
  ctx.quadraticCurveTo(size, 0, size, radius);
  ctx.lineTo(size, size - radius);
  ctx.quadraticCurveTo(size, size, size - radius, size);
  ctx.lineTo(radius, size);
  ctx.quadraticCurveTo(0, size, 0, size - radius);
  ctx.lineTo(0, radius);
  ctx.quadraticCurveTo(0, 0, radius, 0);
  ctx.closePath();
  ctx.fill();
};

/** Load an image into a HTMLImageElement, trying fetch-as-blob first to bypass CORS */
const loadImageSafe = (url: string): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    // ── Strategy 1: fetch → blob URL (no CORS restriction on canvas) ────────
    fetch(url, { mode: "cors", credentials: "omit" })
      .then((res) => {
        if (!res.ok) throw new Error("fetch failed");
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(blobUrl); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(blobUrl); tryDirectLoad(); };
        img.src = blobUrl;
      })
      .catch(() => tryDirectLoad());

    // ── Strategy 2: direct <img> load with crossOrigin ───────────────────────
    const tryDirectLoad = () => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload  = () => resolve(img);
      img.onerror = () => tryNoCors();     // last resort
      img.src = url + (url.includes("?") ? "&" : "?") + "_cb=" + Date.now();
    };

    // ── Strategy 3: no crossOrigin (canvas will be tainted — we try/catch) ──
    const tryNoCors = () => {
      const img = new Image();
      img.onload  = () => resolve(img);
      img.onerror = () => resolve(null);   // give up, background-only
      img.src = url;
    };
  });

/**
 * Builds a rounded-square map marker PNG (as data URL) with:
 *   • A solid coloured background  (layoutColor or dark fallback)
 *   • The app logo centred on top
 *
 * Uses a fetch → blob → canvas pipeline to avoid CORS tainting.
 *
 * @param url         Logo image URL
 * @param layoutColor Background colour (any valid CSS colour string)
 */
export const buildIconWithBg = async (
  url: string,
  layoutColor?: string
): Promise<string> => {
  const bgColor = layoutColor?.trim() || "#1f2937";

  // ── Prepare canvas ─────────────────────────────────────────────────────────
  const canvas = document.createElement("canvas");
  canvas.width  = ICON_SIZE;
  canvas.height = ICON_SIZE;
  const ctx = canvas.getContext("2d")!;

  // Always draw background first
  drawRoundedBg(ctx, bgColor, ICON_SIZE, ICON_RADIUS);

  // ── Load image ─────────────────────────────────────────────────────────────
  const img = await loadImageSafe(url);

  if (img) {
    // Re-draw background to ensure layering is correct
    drawRoundedBg(ctx, bgColor, ICON_SIZE, ICON_RADIUS);

    // Draw logo with circular clip so it fits neatly inside the rounded square
    const drawX = ICON_PAD;
    const drawY = ICON_PAD;
    const drawW = ICON_SIZE - ICON_PAD * 2;
    const drawH = ICON_SIZE - ICON_PAD * 2;

    ctx.save();
    ctx.beginPath();
    ctx.roundRect?.(drawX, drawY, drawW, drawH, 4);   // soft clip (modern browsers)
    ctx.clip();
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    ctx.restore();
  }

  // ── Return data URL (try/catch in case canvas was tainted by strategy 3) ──
  try {
    return canvas.toDataURL("image/png");
  } catch {
    // Canvas tainted — fall back to background-only by re-drawing on a fresh canvas
    const safe = document.createElement("canvas");
    safe.width  = ICON_SIZE;
    safe.height = ICON_SIZE;
    const sctx = safe.getContext("2d")!;
    drawRoundedBg(sctx, bgColor, ICON_SIZE, ICON_RADIUS);
    return safe.toDataURL("image/png");
  }
};